import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
/**
 * Custom hook to manage recipient address state with automatic priority handling:
 *
 * **Priority System:**
 * 1. `recipientAddressFromProps` - Fixed recipient from component props (highest priority)
 * 2. `selectedRecipientAddress` - User's explicit manual selection
 * 3. `walletAddress` or `globalAddress` - Auto-selected fallback
 *
 * **Key Features:**
 * - Automatically manages recipient address based on priority
 * - Preserves user's manual selections
 * - Updates automatically when wallet/global address changes (if no manual selection)
 * - Derived value approach - no useEffect needed, no stale state bugs
 *
 * @example
 * ```tsx
 * const {
 *   selectedRecipientAddress,
 *   setSelectedRecipientAddress,
 *   effectiveRecipientAddress,
 *   resetRecipientAddress
 * } = useRecipientAddressState({
 *   recipientAddressFromProps,
 *   walletAddress,
 *   globalAddress,
 * });
 *
 * // Use effectiveRecipientAddress for display and operations
 * // Use setSelectedRecipientAddress when user explicitly selects
 * // Call resetRecipientAddress when switching tabs or going back
 * ```
 */
export function useRecipientAddressState(t0) {
  const $ = _c(6);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    recipientAddressFromProps,
    walletAddress,
    globalAddress
  } = t1;
  const [selectedRecipientAddress, setSelectedRecipientAddress] = useState(undefined);
  const effectiveRecipientAddress = selectedRecipientAddress || recipientAddressFromProps || walletAddress || globalAddress;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setSelectedRecipientAddress(undefined);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const resetRecipientAddress = t2;
  let t3;
  if ($[3] !== effectiveRecipientAddress || $[4] !== selectedRecipientAddress) {
    t3 = {
      selectedRecipientAddress,
      setSelectedRecipientAddress,
      effectiveRecipientAddress,
      resetRecipientAddress
    };
    $[3] = effectiveRecipientAddress;
    $[4] = selectedRecipientAddress;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}